import { useCallback } from "react"
import type { RequestHandler } from "../useRequestQueue/useRequestQueue"
import { useRequestQueue } from "../useRequestQueue/useRequestQueue"

/**
 * Extracts the argument types from a function type
 * @example Args<(a: string, b: number) => void> = [string, number]
 */
type Args<T> = T extends (...args: infer TArgs) => unknown ? TArgs : never

/**
 * Extracts the resolved type from a Promise-returning function
 * @example PromiseReturn<() => Promise<string>> = string
 */
type PromiseReturn<T> = T extends (...args: never[]) => Promise<infer R> ? R : never

/**
 * Wraps a function to be executed in a request queue.
 * Returns either the successful result or a cancel value if the request is skipped.
 *
 * @param addToRequestQueue - Function to add requests to the queue
 * @param functionToWrap - The async function to wrap
 * @param cancelValue - Value to return when the request is skipped
 * @returns A wrapped function that returns the result or cancel value
 */
export const useWithRequestQueue = <F extends (...args: never[]) => Promise<unknown>, CancelValue>(
	{
		addToRequestQueue,
	}: {
		addToRequestQueue: <T>(request: () => Promise<T>, requestHandler?: RequestHandler<T>) => void
	},
	functionToWrap: F,
	cancelValue: CancelValue,
): ((...args: Args<F>) => Promise<PromiseReturn<F> | CancelValue>) => {
	return useCallback(
		(...args: Args<F>) =>
			new Promise<PromiseReturn<F> | CancelValue>((resolve, reject) => {
				addToRequestQueue(() => functionToWrap(...(args as never[])), {
					onError: reject,
					onSkip: () => resolve(cancelValue),
					onSuccess: (result) => resolve(result as PromiseReturn<F>),
				})
			}),
		[addToRequestQueue, functionToWrap, cancelValue],
	)
}

/**
 * Hook that wraps an async function to execute it in a request queue with skip capability.
 * Useful for preventing race conditions by ensuring only the latest request executes.
 *
 * @param functionToWrap - The async function to wrap
 * @param cancelValue - Value to return when a request is skipped (e.g., when a newer request arrives)
 * @returns A wrapped function that returns either the successful result or the cancel value
 *
 * @example
 * const wrappedFetch = useRequestQueueWrapper(
 *   (id: string) => fetchData(id),
 *   null // return null if request is cancelled
 * )
 * // Later: wrappedFetch('123') - returns Promise<Data | null>
 */
export const useRequestQueueWrapper = <F extends (...args: never[]) => Promise<unknown>, CancelValue>(
	functionToWrap: F,
	cancelValue: CancelValue,
): ((...args: Args<F>) => Promise<PromiseReturn<F> | CancelValue>) => {
	const requestQueue = useRequestQueue({ shouldSkipRequests: true })
	return useWithRequestQueue(requestQueue, functionToWrap, cancelValue)
}
